import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ScalarAnswer } from "@/lib/api/assistantV2";
import { formatScalarValue } from "../utils";
import { AnswerNotes } from "./AnswerNotes";

/** Renders a scalar answer as a "hero" value with its label. */
export function ScalarAnswerCard({ answer }: { answer: ScalarAnswer }) {
  const desc = answer.label_description;
  // When the header falls back to the bare label (no title), let it carry the
  // tooltip too — otherwise only the inline label below does.
  const headerIsLabel = !answer.title && !!answer.label;

  return (
    <TooltipProvider delayDuration={150}>
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-baseline justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-slate-900">
          {headerIsLabel && desc ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2">
                  {answer.label}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{desc}</TooltipContent>
            </Tooltip>
          ) : (
            (answer.title ?? answer.label ?? "Result")
          )}
        </div>
        {answer.study_id && (
          <Badge
            variant="outline"
            className="border-violet-200 text-violet-700 bg-white text-[10px] px-1.5 font-mono"
          >
            {answer.study_id}
            {answer.version ? `@${answer.version}` : ""}
          </Badge>
        )}
      </div>

      <div className="p-6 flex items-baseline gap-4 flex-wrap">
        <div className="text-4xl font-extrabold text-violet-600 tabular-nums break-all">
          {formatScalarValue(answer.value)}
        </div>
        {answer.label &&
          (desc ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-sm text-slate-600 cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2">
                  {answer.label}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{desc}</TooltipContent>
            </Tooltip>
          ) : (
            <div className="text-sm text-slate-600">{answer.label}</div>
          ))}
      </div>

      <AnswerNotes
        notes={answer.notes}
        warnings={answer.warnings}
        diagnostics={answer.diagnostics}
      />
    </div>
    </TooltipProvider>
  );
}
